import {
  ComponentPropsWithoutRef,
  createContext,
  useContext,
  useId,
} from 'react';
import * as Label from '@radix-ui/react-label';
import { motion } from 'motion/react';
import { AnimatePresence } from 'motion/react';

import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

export type FormFieldProps = ComponentPropsWithoutRef<'div'> & {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: string;
  helperText?: React.ReactNode;
  optional?: boolean;
  hideLabel?: boolean;
};

type FormFieldContext = {
  id: string;
  error?: string;
};

const FormFieldContext = createContext<FormFieldContext | null>(null);

export function FormFieldError({ error }: { error?: string }) {
  return (
    <AnimatePresence initial={false}>
      {error ? (
        <motion.div {...ERROR_ANIMATION}>
          <p className={css.error}>{error}</p>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function useFormField() {
  return useContext(FormFieldContext);
}

export function FormField({
  hint,
  label,
  error,
  children,
  helperText,
  optional,
  hideLabel,
  ...props
}: FormFieldProps) {
  const id = useId();

  return (
    <FormFieldContext.Provider value={{ id, error }}>
      <div {...props}>
        <div className={cn(css.labels, hideLabel && !hint && 'sr-only')}>
          <Label.Root
            className={cn(css.label, hideLabel && 'sr-only')}
            htmlFor={id}
          >
            {label}
            {optional && <span className={css.optional}> (optional)</span>}
          </Label.Root>
          {hint ? <span className={css.hint}>{hint}</span> : null}
        </div>
        {helperText ? <p className={css.helperText}>{helperText}</p> : null}
        {children}
        <FormFieldError error={error} />
      </div>
    </FormFieldContext.Provider>
  );
}

const ERROR_ANIMATION = {
  initial: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
  },
  animate: {
    height: 'auto',
    opacity: 1,
    overflow: 'visible',
  },
  exit: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
  },
};
